import { AxiosResponse } from 'axios'
import {
  ESupportChatTab,
  IChat,
  IChatMessage,
  ISendChatMessageRequest,
  ISendSupportMessageRequest,
  ISendSupportMessageResponse,
  ISupportChatResponse,
  ISupportChatUnreadResponse,
  ISupportUnreadCounters,
  IUnreadChatCountResponse
} from 'models/chat'
import { privateApi } from '.'

export const SUPPORT_CHATS_PAGE_SIZE = 20

type TChatAttachments = { images?: File[]; videos?: File[] }

// Фото и видео едут только multipart-формой; вложенные объекты в ней сервер ждёт строкой JSON
const withAttachments = (data: object, { images = [], videos = [] }: TChatAttachments) => {
  if (!images.length && !videos.length) return data

  const form = new FormData()
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined) return
    form.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value))
  })
  images.forEach((image) => form.append('images', image, image.name))
  videos.forEach((video) => form.append('videos', video, video.name))
  return form
}

export const chatApi = {
  getChats(
    skip = 0,
    limit = 20
  ): Promise<AxiosResponse<IChat[]>> {
    // Обращение в поддержку показываем отдельным пунктом
    return privateApi.get<IChat[]>('chat', {
      params: { skip, limit, legacy_support: false }
    })
  },
  getAdminSellerChats(
    sellerId: string,
    skip = 0,
    limit = 50
  ): Promise<AxiosResponse<IChat[]>> {
    return privateApi.get<IChat[]>(`admin/chat/seller/${sellerId}`, {
      params: { skip, limit }
    })
  },
  getAdminSellerUnreadCount(sellerId: string): Promise<AxiosResponse<IUnreadChatCountResponse>> {
    return privateApi.get<IUnreadChatCountResponse>(`admin/chat/seller/${sellerId}/unread-count`)
  },
  getChat(chatId: string): Promise<AxiosResponse<IChat>> {
    return privateApi.get<IChat>(`chat/${chatId}`)
  },
  getAdminChat(chatId: string): Promise<AxiosResponse<IChat>> {
    return privateApi.get<IChat>(`admin/chat/${chatId}`)
  },
  getMessages(
    chatId: string
  ): Promise<AxiosResponse<IChatMessage[]>> {
    return privateApi.get<IChatMessage[]>(`chat/${chatId}/messages`)
  },
  getAdminMessages(
    chatId: string
  ): Promise<AxiosResponse<IChatMessage[]>> {
    return privateApi.get<IChatMessage[]>(`admin/chat/${chatId}/messages`)
  },
  sendMessage(
    chatId: string,
    data: ISendChatMessageRequest,
    attachments: TChatAttachments = {}
  ): Promise<AxiosResponse<IChatMessage>> {
    return privateApi.post<IChatMessage>(`chat/${chatId}/messages`, withAttachments(data, attachments))
  },
  editMessage(
    chatId: string,
    messageId: string,
    text: string
  ): Promise<AxiosResponse<IChatMessage>> {
    return privateApi.patch<IChatMessage>(`chat/${chatId}/messages/${messageId}`, { text })
  },
  markRead(chatId: string): Promise<AxiosResponse<{ success: boolean }>> {
    return privateApi.patch<{ success: boolean }>(`chat/${chatId}/read`)
  },
  getUnreadCount(): Promise<AxiosResponse<IUnreadChatCountResponse>> {
    return privateApi.get<IUnreadChatCountResponse>('chat/unread-count')
  },
  getSupportChat(): Promise<AxiosResponse<ISupportChatResponse>> {
    return privateApi.get<ISupportChatResponse>('chat/support')
  },
  sendSupportMessage(
    data: ISendSupportMessageRequest,
    attachments: TChatAttachments = {}
  ): Promise<AxiosResponse<ISendSupportMessageResponse>> {
    return privateApi.post<ISendSupportMessageResponse>('chat/support/messages', withAttachments(data, attachments))
  },
  getSupportChats(
    tab: ESupportChatTab,
    skip = 0,
    limit = SUPPORT_CHATS_PAGE_SIZE
  ): Promise<AxiosResponse<IChat[]>> {
    return privateApi.get<IChat[]>('admin/support/chats', {
      params: { tab, skip, limit }
    })
  },
  getSupportChatById(chatId: string): Promise<AxiosResponse<IChat>> {
    return privateApi.get<IChat>(`admin/support/chats/${chatId}`)
  },
  getSupportMessages(chatId: string): Promise<AxiosResponse<IChatMessage[]>> {
    return privateApi.get<IChatMessage[]>(`admin/support/chats/${chatId}/messages`)
  },
  sendSupportMessageAsAdmin(
    chatId: string,
    data: ISendSupportMessageRequest,
    attachments: TChatAttachments = {}
  ): Promise<AxiosResponse<IChatMessage>> {
    return privateApi.post<IChatMessage>(`admin/support/chats/${chatId}/messages`, withAttachments(data, attachments))
  },
  editSupportMessageAsAdmin(
    chatId: string,
    messageId: string,
    text: string
  ): Promise<AxiosResponse<IChatMessage>> {
    return privateApi.patch<IChatMessage>(
      `admin/support/chats/${chatId}/messages/${messageId}`,
      { text }
    )
  },
  markSupportRead(chatId: string): Promise<AxiosResponse<{ success: boolean }>> {
    return privateApi.patch<{ success: boolean }>(`admin/support/chats/${chatId}/read`)
  },
  // Сервер возвращает в непрочитанные последнее сообщение автора обращения
  markSupportUnread(chatId: string): Promise<AxiosResponse<ISupportChatUnreadResponse>> {
    return privateApi.patch<ISupportChatUnreadResponse>(`admin/support/chats/${chatId}/unread`)
  },
  getSupportChatWithSeller(sellerId: string): Promise<AxiosResponse<ISupportChatResponse>> {
    return privateApi.get<ISupportChatResponse>(`admin/support/sellers/${sellerId}/chat`)
  },
  // Обращения с продавцом ещё нет: сервер заводит его этим сообщением
  sendFirstSupportMessageToSeller(
    sellerId: string,
    data: ISendSupportMessageRequest,
    attachments: TChatAttachments = {}
  ): Promise<AxiosResponse<ISendSupportMessageResponse>> {
    return privateApi.post<ISendSupportMessageResponse>(
      `admin/support/sellers/${sellerId}/messages`,
      withAttachments(data, attachments)
    )
  },
  getSupportUnreadCount(): Promise<AxiosResponse<ISupportUnreadCounters>> {
    return privateApi.get<ISupportUnreadCounters>('admin/support/unread-count')
  }
}
